import { ChevronDown, ChevronUp } from "lucide-react";
import { ScoreHistogram } from "./ScoreHistogram";

interface Props {
  paperCount: number;
  /** Working-set-scoped count before filters; if equal to paperCount, the
      "of Y active" suffix is suppressed. */
  totalCount?: number;
  scores: number[];
  yearRange: [number, number] | null;
  supportCount: number;
  counterCount: number;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
}

export function AnalyzeScopeStrip({
  paperCount,
  totalCount,
  scores,
  yearRange,
  supportCount,
  counterCount,
  expanded,
  onToggle,
}: Props) {
  const validScores = scores.filter((s) => s > 0);
  const minScore = validScores.length ? Math.min(...validScores) : null;
  const maxScore = validScores.length ? Math.max(...validScores) : null;
  const showCamps = supportCount > 0 || counterCount > 0;
  const showHistogram = validScores.length >= 10;

  return (
    <button
      type="button"
      onClick={() => onToggle(!expanded)}
      className="flex w-full items-center gap-4 px-3.5 py-2.5 bg-muted/30 text-left hover:bg-muted/50 transition-colors"
      aria-expanded={expanded}
    >
      <span className="text-sm font-semibold">
        {paperCount} papers
        {totalCount != null && totalCount > paperCount && (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            of {totalCount} active
          </span>
        )}
      </span>

      {showHistogram && (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="text-[10px] uppercase tracking-wide font-semibold">
            Score
          </span>
          <ScoreHistogram scores={validScores} />
          {minScore != null && maxScore != null && (
            <span className="text-[10px]">
              {minScore}–{maxScore}
            </span>
          )}
        </span>
      )}

      {yearRange && (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="text-[10px] uppercase tracking-wide font-semibold">
            Years
          </span>
          {yearRange[0]}–{yearRange[1]}
        </span>
      )}

      {showCamps && (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="text-[10px] uppercase tracking-wide font-semibold">
            Camps
          </span>
          {supportCount} supports · {counterCount} counters
        </span>
      )}

      <span className="ml-auto text-xs text-muted-foreground inline-flex items-center gap-1">
        {expanded ? (
          <>
            <ChevronUp className="size-3" /> Hide table
          </>
        ) : (
          <>
            <ChevronDown className="size-3" /> Show table
          </>
        )}
      </span>
    </button>
  );
}
